import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, Receipt, Check, Trash2, Mail } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, fmtDate } from "@/lib/api";
import { PageHeader, Panel, ConfirmDelete } from "@/components/admin/AdminUI";

function WaiterCalls() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/waiter-calls").then(({ data }) => setRows(data));
  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, []);
  const done = async (id) => {
    await api.patch(`/admin/waiter-calls/${id}`);
    load();
  };
  const remove = async (id) => {
    await api.delete(`/admin/waiter-calls/${id}`);
    load();
  };
  if (!rows.length) return <Panel className="p-10 text-center text-sm text-[#6B5E56]">Henüz çağrı yok. QR menüden gelen garson/hesap talepleri burada görünür (10 sn'de bir yenilenir).</Panel>;
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {rows.map((w) => (
        <Panel key={w.id} className={`p-5 ${w.done ? "opacity-50" : "border-[#D4AF37]"}`} data-testid={`admin-waiter-call-${w.id}`}>
          <div className="flex items-center gap-3">
            <div className={`h-11 w-11 rounded-full grid place-items-center ${w.type === "hesap" ? "bg-[#C84B31] text-white" : "bg-[#D4AF37] text-[#140D0B]"}`}>
              {w.type === "hesap" ? <Receipt size={18} /> : <Bell size={18} />}
            </div>
            <div className="flex-1">
              <p className="font-display text-lg">Masa {w.table}</p>
              <p className="text-xs text-[#6B5E56]">{w.type === "hesap" ? "Hesap istiyor" : "Garson çağırıyor"} · {fmtDate(w.created_at)}</p>
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            {!w.done && <button onClick={() => done(w.id)} className="flex-1 inline-flex items-center justify-center gap-1 rounded-md bg-[#1A1513] text-white py-2 text-xs" data-testid={`admin-waiter-done-${w.id}`}><Check size={14} /> İlgilenildi</button>}
            <button onClick={() => remove(w.id)} className="p-2 rounded-md hover:bg-red-50 text-[#C84B31]" aria-label="Sil" data-testid={`admin-waiter-delete-${w.id}`}><Trash2 size={14} /></button>
          </div>
        </Panel>
      ))}
    </div>
  );
}

function MessagesList() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/messages").then(({ data }) => setRows(data));
  useEffect(() => {
    load();
  }, []);
  const read = async (id) => {
    await api.patch(`/admin/messages/${id}`);
    load();
  };
  const remove = async (id) => {
    await api.delete(`/admin/messages/${id}`);
    toast.success("Mesaj silindi");
    load();
  };
  if (!rows.length) return <Panel className="p-10 text-center text-sm text-[#6B5E56]">Henüz mesaj yok.</Panel>;
  return (
    <div className="space-y-3">
      {rows.map((m) => (
        <Panel key={m.id} className={`p-5 ${!m.read ? "border-l-4 border-l-[#D4AF37]" : ""}`} data-testid={`admin-message-${m.id}`}>
          <div className="flex justify-between gap-3">
            <div>
              <p className="font-semibold">{m.name}</p>
              <p className="text-xs text-[#6B5E56]">{m.phone} {m.email} · {fmtDate(m.created_at)}</p>
            </div>
            <div className="flex gap-1">
              {!m.read && <button onClick={() => read(m.id)} className="p-2 rounded-md hover:bg-[#F3EDE2]" aria-label="Okundu" data-testid={`admin-message-read-${m.id}`}><Check size={15} /></button>}
              <ConfirmDelete onConfirm={() => remove(m.id)}>
                <button className="p-2 rounded-md hover:bg-red-50 text-[#C84B31]" aria-label="Sil" data-testid={`admin-message-delete-${m.id}`}><Trash2 size={15} /></button>
              </ConfirmDelete>
            </div>
          </div>
          <p className="mt-3 text-sm">{m.message}</p>
        </Panel>
      ))}
    </div>
  );
}

export default function Messages() {
  return (
    <div data-testid="admin-messages-page">
      <PageHeader title="Garson Çağrıları & Mesajlar" desc="QR menüden masa çağrıları ve siteden gelen mesajlar." />
      <Tabs defaultValue="calls">
        <TabsList className="mb-5">
          <TabsTrigger value="calls" data-testid="admin-tab-waiter-calls"><Bell size={14} className="mr-2" /> Garson Çağrıları</TabsTrigger>
          <TabsTrigger value="messages" data-testid="admin-tab-messages"><Mail size={14} className="mr-2" /> Mesajlar</TabsTrigger>
        </TabsList>
        <TabsContent value="calls"><WaiterCalls /></TabsContent>
        <TabsContent value="messages"><MessagesList /></TabsContent>
      </Tabs>
    </div>
  );
}
